import React from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import SeoMetadata from "@/components/common/SeoMetadata";
import UscoreAdvertisement from "@/components/home/UscoreAdvertisement";
import { useIsMobile } from "@/hooks/use-mobile";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Zap, Clock, AlertCircle, CheckCircle2, FileText, ArrowRight, Wallet, Calendar, Scale } from "lucide-react";
import RelatedArticles from "@/components/articles/RelatedArticles";

const PikalainaPage = () => {
  const isMobile = useIsMobile();
  
  // Fade in animation variant
  const fadeIn = {
    hidden: { opacity: 0, y: 20 },
    visible: (i: number) => ({
      opacity: 1,
      y: 0,
      transition: {
        delay: i * 0.1,
        duration: 0.5,
      }
    })
  };
  
  return (
    <>
      <SeoMetadata
        title="Pikalaina - Nopea rahoitus yllättäviin menoihin"
        description="Pikalaina on nopea ja joustava rahoitusmuoto, joka soveltuu erityisesti yllättäviin menoihin ja tilanteisiin, joissa rahoitusta tarvitaan nopeasti. Se on tyypillisesti pienempi laina, joka myönnetään nopealla aikataululla."
        keywords={["pikalaina", "pienlaina", "nopea laina", "joustava rahoitus", "äkilliset menot", "rahoitusmuoto", "edullinen pikalaina"]}
        canonicalUrl="https://lainasampo.fi/pikalaina"
        imageUrl="https://lainasampo.fi/images/pikalaina.jpg"
      />
      
      <div className="min-h-screen bg-white p-4 md:p-8">
        <div className="subpage-container">
          <motion.div
            initial="hidden"
            animate="visible"
            custom={0}
            variants={fadeIn}
          >
            <h1 className="text-3xl font-bold mb-6 text-blue-700">Pikalaina</h1>
          </motion.div>
          
          <motion.div 
            className="relative bg-gradient-to-r from-blue-600 to-blue-700 p-8 rounded-lg mb-8 overflow-hidden text-white"
            initial="hidden"
            animate="visible" 
            custom={1}
            variants={fadeIn}
          >
            <div className="absolute right-0 top-0 opacity-10 md:opacity-20">
              <Zap className="w-48 h-48" />
            </div>
            <div className="relative z-10">
              <h2 className="text-2xl font-semibold mb-4">Nopea rahoitus yllättäviin menoihin</h2>
              <p className="text-blue-100 mb-4">
                Pikalaina on nopea ja joustava rahoitusmuoto, joka soveltuu erityisesti yllättäviin menoihin ja tilanteisiin, 
                joissa rahoitusta tarvitaan nopeasti. Se on tyypillisesti pienempi laina, joka myönnetään nopealla aikataululla.
              </p>
              <p className="text-blue-100">
                Lainasampo.fi:n kautta voit vertailla eri rahoituslaitosten pikalainoja ja löytää edullisimman vaihtoehdon 
                nopeaan rahoitustarpeeseen.
              </p>
            </div>
          </motion.div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
            <motion.div initial="hidden" animate="visible" custom={2} variants={fadeIn}>
              <Card className="h-full bg-white hover:shadow-md transition-shadow">
                <CardHeader>
                  <div className="flex items-center">
                    <Zap className="h-5 w-5 mr-2 text-blue-600" />
                    <CardTitle className="text-blue-700">Pikalainan ominaisuudet</CardTitle>
                  </div>
                  <CardDescription>Mitä sinun tulisi tietää pikalainoista?</CardDescription>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-3">
                    <li className="flex items-start">
                      <CheckCircle2 className="h-5 w-5 mr-2 text-green-500 flex-shrink-0 mt-0.5" />
                      <span className="text-gray-700">
                        <span className="font-medium">Nopea hakuprosessi</span> - päätös usein minuuteissa
                      </span>
                    </li>
                    <li className="flex items-start">
                      <CheckCircle2 className="h-5 w-5 mr-2 text-green-500 flex-shrink-0 mt-0.5" />
                      <span className="text-gray-700">
                        <span className="font-medium">Rahat tilille nopeasti</span> - jopa saman päivän aikana
                      </span>
                    </li>
                    <li className="flex items-start">
                      <CheckCircle2 className="h-5 w-5 mr-2 text-green-500 flex-shrink-0 mt-0.5" />
                      <span className="text-gray-700">
                        <span className="font-medium">Lainasummat</span> - tyypillisesti 100-2000 euroa
                      </span>
                    </li>
                    <li className="flex items-start">
                      <CheckCircle2 className="h-5 w-5 mr-2 text-green-500 flex-shrink-0 mt-0.5" />
                      <span className="text-gray-700">
                        <span className="font-medium">Lyhyet laina-ajat</span> - yleensä 1-12 kuukautta
                      </span>
                    </li>
                    <li className="flex items-start">
                      <CheckCircle2 className="h-5 w-5 mr-2 text-green-500 flex-shrink-0 mt-0.5" />
                      <span className="text-gray-700">
                        <span className="font-medium">Ei vakuuksia</span> - ei vaadi takaajia tai vakuuksia
                      </span>
                    </li>
                  </ul>
                  <div className="mt-4 pt-4 border-t border-gray-100">
                    <p className="text-gray-700 text-sm">
                      Pikalainat ovat joustava rahoitusmuoto, mutta niiden korko on usein korkeampi kuin perinteisten 
                      lainojen. Vertaile eri vaihtoehtoja huolellisesti.
                    </p>
                  </div>
                </CardContent>
              </Card>
            </motion.div>
            
            <motion.div initial="hidden" animate="visible" custom={3} variants={fadeIn}>
              <Card className="h-full bg-white hover:shadow-md transition-shadow">
                <CardHeader>
                  <div className="flex items-center">
                    <Clock className="h-5 w-5 mr-2 text-blue-600" />
                    <CardTitle className="text-blue-700">Näin haet pikalainaa</CardTitle>
                  </div>
                  <CardDescription>Pikalainan hakeminen on nopeaa</CardDescription>
                </CardHeader>
                <CardContent>
                  <ol className="space-y-3 list-decimal pl-5">
                    <li className="text-gray-700">
                      <span className="font-medium">Täytä lainahakemus</span> verkossa Lainasampo.fi:ssä
                    </li>
                    <li className="text-gray-700">
                      <span className="font-medium">Vertaile eri lainanantajien</span> tarjouksia
                    </li>
                    <li className="text-gray-700">
                      <span className="font-medium">Valitse itsellesi sopivin</span> pikalainatarjous
                    </li>
                    <li className="text-gray-700">
                      <span className="font-medium">Tunnistaudu</span> verkkopankkitunnuksilla
                    </li>
                    <li className="text-gray-700">
                      <span className="font-medium">Rahat siirretään tilillesi</span> hyväksytyn päätöksen jälkeen
                    </li>
                  </ol>
                  <div className="mt-4 pt-4 border-t border-gray-100">
                    <p className="text-gray-700 text-sm">
                      Kilpailuttamalla pikalainat Lainasampo.fi:n kautta voit säästää korkokuluissa huomattavasti 
                      verrattuna suoraan yhdeltä lainanantajalta haettuun lainaan.
                    </p>
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          </div>
          
          <motion.div 
            initial="hidden" 
            animate="visible" 
            custom={4} 
            variants={fadeIn}
            className="mb-8"
          >
            <Card>
              <CardHeader>
                <div className="flex items-center">
                  <AlertCircle className="h-5 w-5 mr-2 text-blue-600" />
                  <CardTitle className="text-xl text-blue-700">Mitä tulee huomioida pikalainaa hakiessa?</CardTitle>
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <h3 className="font-medium text-blue-700 mb-2">Todellinen vuosikorko</h3>
                  <p className="text-gray-700">
                    Kiinnitä erityistä huomiota pikalainan todelliseen vuosikorkoon. Se sisältää koron lisäksi 
                    kaikki muut lainan kustannukset, kuten nosto- ja käsittelymaksut. Todellinen vuosikorko 
                    on paras mittari lainan kokonaiskustannusten vertailuun.
                  </p>
                </div>
                
                <div>
                  <h3 className="font-medium text-blue-700 mb-2">Takaisinmaksukyky</h3>
                  <p className="text-gray-700">
                    Varmista, että pystyt maksamaan lainan takaisin sovitussa aikataulussa. Pikalainojen 
                    myöhästyneistä maksuista veloitetaan usein korkeita viivästyskorkoja ja muistutusmaksuja.
                  </p>
                </div>
                
                <div>
                  <h3 className="font-medium text-blue-700 mb-2">Lainan kustannukset</h3>
                  <p className="text-gray-700">
                    Huomioi kaikki lainaan liittyvät kustannukset: korko, nostopalkkiot, käsittelymaksut ja 
                    muut mahdolliset kulut. Pikalainoissa kulut voivat vaihdella merkittävästi eri 
                    lainanantajien välillä.
                  </p>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 pt-4">
                  <div className="bg-blue-50 p-4 rounded-lg">
                    <div className="flex items-start">
                      <Wallet className="h-5 w-5 mr-2 text-blue-600 flex-shrink-0 mt-0.5" />
                      <div>
                        <p className="font-medium text-blue-700 mb-1">Lainan järkevyys</p>
                        <p className="text-sm text-gray-700">
                          Harkitse, onko pikalaina paras ratkaisu tilanteeseesi. Akuutteihin tilanteisiin se voi olla hyvä, 
                          mutta jatkuviin rahavaikeuksiin se ei ole kestävä ratkaisu.
                        </p>
                      </div>
                    </div>
                  </div>
                  
                  <div className="bg-blue-50 p-4 rounded-lg">
                    <div className="flex items-start">
                      <Calendar className="h-5 w-5 mr-2 text-blue-600 flex-shrink-0 mt-0.5" />
                      <div>
                        <p className="font-medium text-blue-700 mb-1">Maksuajat</p>
                        <p className="text-sm text-gray-700">
                          Pikalainat ovat yleensä lyhytaikaisia. Mieti, mitä tapahtuu, jos et pysty maksamaan lainaa takaisin ajallaan.
                        </p>
                      </div>
                    </div>
                  </div>
                  
                  <div className="bg-blue-50 p-4 rounded-lg">
                    <div className="flex items-start">
                      <Scale className="h-5 w-5 mr-2 text-blue-600 flex-shrink-0 mt-0.5" />
                      <div>
                        <p className="font-medium text-blue-700 mb-1">Lainanantajan luotettavuus</p>
                        <p className="text-sm text-gray-700">
                          Varmista, että lainanantaja on luotettava ja toimii Finanssivalvonnan alaisuudessa.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </motion.div>
          
          <motion.div
            initial="hidden"
            animate="visible"
            custom={5}
            variants={fadeIn}
            className="mb-8"
          >
            <RelatedArticles 
              productType="pikalaina" 
              title="Hyödyllisiä artikkeleita pikalainoista"
            />
          </motion.div>
          
          <motion.div
            initial="hidden"
            animate="visible"
            custom={6}
            variants={fadeIn}
            className="bg-gradient-to-r from-blue-600 to-blue-700 text-white p-6 rounded-lg"
          >
            <div className="flex flex-col md:flex-row items-center justify-between">
              <div className="mb-4 md:mb-0">
                <h2 className="text-xl font-semibold mb-2">Kilpailuta pikalainasi nyt!</h2>
                <p className="text-blue-100">
                  Vertailemalla eri lainanantajien pikalainoja voit säästää korkokuluissa merkittävästi. 
                  Täytä hakemus nyt ja saat tarjoukset nopeasti!
                </p>
              </div>
              <motion.div
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                <Link
                  to="/kilpailuta"
                  className="inline-block bg-white text-blue-600 px-6 py-3 rounded-md font-medium hover:bg-blue-50 transition-colors"
                >
                  Hae pikalainaa <ArrowRight className="inline-block ml-1 h-4 w-4" />
                </Link>
              </motion.div>
            </div>
          </motion.div>
        </div>
        
        <div className="container mx-auto py-8 px-4">
          <UscoreAdvertisement />
        </div>
      </div>
    </>
  );
};

export default PikalainaPage;
